import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  Building2,
  DollarSign,
  Home,
  Lock,
  Wrench,
  FileText,
  Settings,
  CreditCard,
} from "lucide-react";

export type AgentType =
  | "comercial"
  | "captacion"
  | "mantenimientos"
  | "administrativo"
  | "cartera"
  | "custom"
  | "development-comercial";

type Step1AgentTypeSelectionProps = {
  onSelect: (agentType: AgentType) => void;
  merchantType?: "rentals" | "development";
  disabledTypes?: {
    comercial?: boolean;
    captacion?: boolean;
    mantenimientos?: boolean;
    administrativo?: boolean;
    cartera?: boolean;
    custom?: boolean;
  };
};

const Step1AgentTypeSelection = ({
  onSelect,
  merchantType,
  disabledTypes = {},
}: Step1AgentTypeSelectionProps) => {
  const isComercialDisabled = disabledTypes.comercial || false;
  const isCaptacionDisabled = disabledTypes.captacion || false;
  const isMantenimientosDisabled = disabledTypes.mantenimientos || false;
  const isAdministrativoDisabled = disabledTypes.administrativo || false;
  const isCarteraDisabled = disabledTypes.cartera || false;
  const isDevelopmentMerchant = merchantType === "development";

  const handleComercialClick = () => {
    if (!isComercialDisabled) {
      onSelect("comercial");
    }
  };

  const handleCaptacionClick = () => {
    if (!isCaptacionDisabled) {
      onSelect("captacion");
    }
  };

  const handleMantenimientosClick = () => {
    if (!isMantenimientosDisabled) {
      onSelect("mantenimientos");
    }
  };

  const handleAdministrativoClick = () => {
    if (!isAdministrativoDisabled) {
      onSelect("administrativo");
    }
  };

  const handleCarteraClick = () => {
    if (!isCarteraDisabled) {
      onSelect("cartera");
    }
  };

  const handleCustomClick = () => {
    onSelect("custom");
  };

  // Development merchants get a single, dedicated card. Rentals merchants see the existing
  // catalog. We don't mix the two — the toolsets, prompts, and graphs are different.
  if (isDevelopmentMerchant) {
    return (
      <div className="flex flex-col gap-6">
        <p className="mb-4 text-lg font-semibold">
          Selecciona el tipo de agente que deseas crear
        </p>

        <div className="grid max-w-[1200px] grid-cols-1 gap-6 md:grid-cols-2">
          <div
            className="relative cursor-pointer rounded-lg border-2 border-gray-200 bg-white p-6 transition-all hover:-translate-y-px hover:border-gray-400 hover:shadow-md"
            onClick={() => onSelect("development-comercial")}
          >
            <div className="mb-2 flex items-center gap-3">
              <div className="flex items-center justify-center rounded bg-[#5C28F5] p-2">
                <Building2 className="size-5 text-white" />
              </div>
              <p className="text-base font-bold text-gray-800">Agente Comercial (Desarrollos)</p>
            </div>

            <Badge className="mb-3 w-fit rounded-full border-green-200 bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
              Recomendado
            </Badge>

            <p className="mb-3 text-sm leading-snug text-gray-600">
              Vincula el agente a un proyecto y a un número de WhatsApp. Cada proyecto puede tener
              un solo agente.
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <Badge className="border-blue-200 bg-blue-100 text-xs text-blue-800">
                Desarrollos 1:1 con proyecto
              </Badge>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <p className="text-lg font-semibold mb-4">
        Selecciona el tipo de agente que deseas crear
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[1200px]">
        {/* Agente Comercial Card */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  "rounded-lg border-2 border-gray-200 p-6 transition-all relative",
                  isComercialDisabled
                    ? "bg-gray-50 cursor-not-allowed opacity-60"
                    : "bg-white cursor-pointer hover:border-gray-400 hover:-translate-y-px hover:shadow-md"
                )}
                onClick={handleComercialClick}
              >
                {isComercialDisabled && (
                  <div className="absolute top-4 right-4 bg-gray-400 rounded-full p-1.5 flex items-center justify-center">
                    <Lock className="size-3.5 text-white" />
                  </div>
                )}
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className={cn(
                      "rounded p-2 flex items-center justify-center",
                      isComercialDisabled ? "bg-gray-400" : "bg-[#5C28F5]"
                    )}
                  >
                    <DollarSign className="size-5 text-white" />
                  </div>
                  <p className="text-base font-bold text-gray-800">
                    Agente Comercial
                  </p>
                </div>

                <Badge className="bg-green-100 text-green-800 border-green-200 rounded-full px-2 py-0.5 text-xs font-medium mb-3 w-fit">
                  Popular
                </Badge>

                <p className="text-sm text-gray-600 mb-3 leading-snug">
                  Especializado en ventas y arriendos
                </p>

                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-xs">
                    Ventas y arriendos
                  </Badge>
                </div>
              </div>
            </TooltipTrigger>
            {isComercialDisabled && (
              <TooltipContent>
                Ya existe un agente comercial para tu comercio
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>

        {/* Agente de Captacion Card */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  "rounded-lg border-2 border-gray-200 p-6 transition-all relative",
                  isCaptacionDisabled
                    ? "bg-gray-50 cursor-not-allowed opacity-60"
                    : "bg-white cursor-pointer hover:border-gray-400 hover:-translate-y-px hover:shadow-md"
                )}
                onClick={handleCaptacionClick}
              >
                {isCaptacionDisabled && (
                  <div className="absolute top-4 right-4 bg-gray-400 rounded-full p-1.5 flex items-center justify-center">
                    <Lock className="size-3.5 text-white" />
                  </div>
                )}
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className={cn(
                      "rounded p-2 flex items-center justify-center",
                      isCaptacionDisabled ? "bg-gray-400" : "bg-[#5C28F5]"
                    )}
                  >
                    <Home className="size-5 text-white" />
                  </div>
                  <p className="text-base font-bold text-gray-800">
                    Agente de Captacion
                  </p>
                </div>

                <p className="text-sm text-gray-600 mb-3 leading-snug">
                  Especializado en captacion de inmuebles para ventas y arriendo
                </p>

                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-xs">
                    Ventas y Arriendos
                  </Badge>
                </div>
              </div>
            </TooltipTrigger>
            {isCaptacionDisabled && (
              <TooltipContent>
                Ya existe un agente de captacion para tu comercio
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>

        {/* Agente de Mantenimientos Card */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  "rounded-lg border-2 border-gray-200 p-6 transition-all relative",
                  isMantenimientosDisabled
                    ? "bg-gray-50 cursor-not-allowed opacity-60"
                    : "bg-white cursor-pointer hover:border-gray-400 hover:-translate-y-px hover:shadow-md"
                )}
                onClick={handleMantenimientosClick}
              >
                {isMantenimientosDisabled && (
                  <div className="absolute top-4 right-4 bg-gray-400 rounded-full p-1.5 flex items-center justify-center">
                    <Lock className="size-3.5 text-white" />
                  </div>
                )}
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className={cn(
                      "rounded p-2 flex items-center justify-center",
                      isMantenimientosDisabled ? "bg-gray-400" : "bg-[#5C28F5]"
                    )}
                  >
                    <Wrench className="size-5 text-white" />
                  </div>
                  <p className="text-base font-bold text-gray-800">
                    Agente de Mantenimientos
                  </p>
                </div>

                <p className="text-sm text-gray-600 mb-3 leading-snug">
                  Encargado de recibir y registrar solicitudes de mantenimientos
                </p>

                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-orange-100 text-orange-800 border-orange-200 text-xs">
                    Mantenimientos
                  </Badge>
                </div>
              </div>
            </TooltipTrigger>
            {isMantenimientosDisabled && (
              <TooltipContent>
                Ya existe un agente de mantenimientos para tu comercio
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>

        {/* Agente Administrativo Card */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  "rounded-lg border-2 border-gray-200 p-6 transition-all relative",
                  isAdministrativoDisabled
                    ? "bg-gray-50 cursor-not-allowed opacity-60"
                    : "bg-white cursor-pointer hover:border-gray-400 hover:-translate-y-px hover:shadow-md"
                )}
                onClick={handleAdministrativoClick}
              >
                {isAdministrativoDisabled && (
                  <div className="absolute top-4 right-4 bg-gray-400 rounded-full p-1.5 flex items-center justify-center">
                    <Lock className="size-3.5 text-white" />
                  </div>
                )}
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className={cn(
                      "rounded p-2 flex items-center justify-center",
                      isAdministrativoDisabled ? "bg-gray-400" : "bg-[#5C28F5]"
                    )}
                  >
                    <FileText className="size-5 text-white" />
                  </div>
                  <p className="text-base font-bold text-gray-800">
                    Agente Administrativo
                  </p>
                </div>

                <p className="text-sm text-gray-600 mb-3 leading-snug">
                  Agente de proposito general para responder preguntas administrativas o redirigir a un
                  agente humano bajo condiciones especificas
                </p>

                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-xs">
                    General
                  </Badge>
                </div>
              </div>
            </TooltipTrigger>
            {isAdministrativoDisabled && (
              <TooltipContent>
                Ya existe un agente administrativo para tu comercio
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>

        {/* Agente de Cartera Card */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  "rounded-lg border-2 border-gray-200 p-6 transition-all relative",
                  isCarteraDisabled
                    ? "bg-gray-50 cursor-not-allowed opacity-60"
                    : "bg-white cursor-pointer hover:border-gray-400 hover:-translate-y-px hover:shadow-md"
                )}
                onClick={handleCarteraClick}
              >
                {isCarteraDisabled && (
                  <div className="absolute top-4 right-4 bg-gray-400 rounded-full p-1.5 flex items-center justify-center">
                    <Lock className="size-3.5 text-white" />
                  </div>
                )}
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className={cn(
                      "rounded p-2 flex items-center justify-center",
                      isCarteraDisabled ? "bg-gray-400" : "bg-[#5C28F5]"
                    )}
                  >
                    <CreditCard className="size-5 text-white" />
                  </div>
                  <p className="text-base font-bold text-gray-800">
                    Agente de Cartera
                  </p>
                </div>

                <p className="text-sm text-gray-600 mb-3 leading-snug">
                  Especializado en gestion de cartera y cobranza
                </p>

                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-teal-100 text-teal-800 border-teal-200 text-xs">
                    Cartera
                  </Badge>
                </div>
              </div>
            </TooltipTrigger>
            {isCarteraDisabled && (
              <TooltipContent>
                Ya existe un agente de cartera para tu comercio
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>

        {/* Agente personalizado */}
        <div
          className="rounded-lg border-2 border-gray-200 bg-white p-6 cursor-pointer transition-all hover:border-gray-400 hover:-translate-y-px hover:shadow-md"
          onClick={handleCustomClick}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="rounded p-2 flex items-center justify-center bg-[#5C28F5]">
              <Settings className="size-5 text-white" />
            </div>
            <p className="text-base font-bold text-gray-800">
              Agente personalizado
            </p>
          </div>

          <p className="text-sm text-gray-600 mb-3 leading-snug">
            Define tu propio proceso (pipeline) y posiciones para un flujo a medida
          </p>

          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-gray-100 text-gray-800 border-gray-200 text-xs">
              Personalizado
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step1AgentTypeSelection;
